import { fabric } from 'fabric';

import { drawingTool } from '~/core/engine/DrawingTool';
import { CircleAction, DiagonalCornerOptions, StepName } from '~/core/enums';
import { getCornerType } from '~/core/helpers';
import {
  CornerRadiusModel,
  DiagonalCornerModel,
  DrawingShape,
} from '~/core/models';
import { CustomSideModel } from '~/core/models/predefined-custom-side.model';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type SelectionCircleStep =
  | StepName.designCornerRadius
  | StepName.designDiagonalCorner
  | StepName.designCustomSide;

export type SelectionCircleRenderer = (drawingShape: DrawingShape) => void;

export interface SelectionCircleRenderersConfig {
  canvas: fabric.Canvas;
  action: CircleAction;
  selectedCornerRadius: CornerRadiusModel | undefined;
  defaultCornerRadii: CornerRadiusModel | undefined;
  selectedDiagonalCorner: DiagonalCornerModel | undefined;
  defaultDiagonalCorner: DiagonalCornerModel | undefined;
  selectedCustomSide: CustomSideModel | undefined;
}

// ============================================================================
// REMOVAL CORNER HELPERS
// ============================================================================

const getRemovalCorner = <T>(
  action: CircleAction,
  defaultValue: T | undefined
): T | undefined => (action === CircleAction.REMOVE ? defaultValue : undefined);

const getDiagonalCornerType = (
  action: CircleAction,
  selectedDiagonalCorner: DiagonalCornerModel
): DiagonalCornerOptions =>
  action === CircleAction.REMOVE
    ? DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER
    : getCornerType(selectedDiagonalCorner);

// ============================================================================
// STEP RENDERER FACTORIES
// ============================================================================

const createCornerRadiusRenderer = (
  canvas: fabric.Canvas,
  selectedCornerRadius: CornerRadiusModel,
  action: CircleAction,
  defaultCornerRadii: CornerRadiusModel | undefined
): SelectionCircleRenderer => {
  const removalCorner = getRemovalCorner(action, defaultCornerRadii);

  return (drawingShape: DrawingShape) =>
    drawingTool.addCirclesToOutlinePointsOfShape(
      canvas,
      drawingShape,
      selectedCornerRadius,
      DiagonalCornerOptions.OUTSIDE_CORNER,
      { removalCorner }
    );
};

const createDiagonalCornerRenderer = (
  canvas: fabric.Canvas,
  selectedDiagonalCorner: DiagonalCornerModel,
  action: CircleAction,
  defaultDiagonalCorner: DiagonalCornerModel | undefined
): SelectionCircleRenderer => {
  const diagonalType = getDiagonalCornerType(action, selectedDiagonalCorner);
  const removalCorner = getRemovalCorner(action, defaultDiagonalCorner);

  return (drawingShape: DrawingShape) =>
    drawingTool.addCirclesToOutlinePointsOfShape(
      canvas,
      drawingShape,
      selectedDiagonalCorner,
      diagonalType,
      { removalCorner }
    );
};

const createCustomSideRenderer =
  (
    canvas: fabric.Canvas,
    selectedCustomSide: CustomSideModel,
    action: CircleAction
  ): SelectionCircleRenderer =>
  (drawingShape: DrawingShape) =>
    drawingTool.addCirclesToEdgesOfShape(
      canvas,
      drawingShape,
      selectedCustomSide,
      action
    );

// ============================================================================
// MAIN BUILDER FUNCTION
// ============================================================================

export const buildSelectionCircleRenderers = (
  config: SelectionCircleRenderersConfig
): Record<SelectionCircleStep, SelectionCircleRenderer | undefined> => {
  const {
    canvas,
    action,
    selectedCornerRadius,
    defaultCornerRadii,
    selectedDiagonalCorner,
    defaultDiagonalCorner,
    selectedCustomSide,
  } = config;

  return {
    [StepName.designCornerRadius]: selectedCornerRadius
      ? createCornerRadiusRenderer(
          canvas,
          selectedCornerRadius,
          action,
          defaultCornerRadii
        )
      : undefined,

    [StepName.designDiagonalCorner]: selectedDiagonalCorner
      ? createDiagonalCornerRenderer(
          canvas,
          selectedDiagonalCorner,
          action,
          defaultDiagonalCorner
        )
      : undefined,

    [StepName.designCustomSide]: selectedCustomSide
      ? createCustomSideRenderer(canvas, selectedCustomSide, action)
      : undefined,
  };
};
